import { axiosInstance } from "../../../api/axiosInstance";
import { GradeDayPeriods, Period } from "../types";

/**
 * Bell schedule and grade periods (Academics → Setup). Admin only.
 *
 *   GET /periods                    → Period[]                 the bell schedule, by start time
 *   PUT /periods          Period[]  → Period[]                 replaces it: rows without id are new, missing
 *                                                              ids are deleted (409 if a grade uses one)
 *   GET /grade-periods              → { [gradeCode]: GradeDayPeriods }
 *   PUT /grades/{code}/periods  GradeDayPeriods → GradeDayPeriods   (409 if the timetable uses a removed one)
 */

// The backend drops false/empty values from JSON (jackson non_empty) — put them back.
const withDefaults = (period: Period): Period => ({ ...period, isBreak: Boolean(period.isBreak) });

export const fetchPeriods = async (): Promise<Period[]> => {
  const response = await axiosInstance.get<Period[]>("/periods");
  return (response.data ?? []).map(withDefaults);
};

/** Replaces the whole bell schedule; returns it as saved (ids filled, renumbered by start time). */
export const savePeriods = async (periods: Period[]): Promise<Period[]> => {
  const response = await axiosInstance.put<Period[]>("/periods", periods);
  return (response.data ?? []).map(withDefaults);
};

/** Every grade's periods per day, keyed by grade code. */
export const fetchAllGradePeriods = async (): Promise<Record<string, GradeDayPeriods>> => {
  const response = await axiosInstance.get<Record<string, GradeDayPeriods>>("/grade-periods");
  return response.data ?? {};
};

/** Makes the grade's periods exactly `days`; returns them as saved. */
export const saveGradePeriods = async (gradeCode: string, days: GradeDayPeriods): Promise<GradeDayPeriods> => {
  const response = await axiosInstance.put<GradeDayPeriods>(`/grades/${encodeURIComponent(gradeCode)}/periods`, days);
  return response.data ?? {};
};
